<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { ref, watch } from 'vue'
import IndexProgrammeCard from 'components/home/IndexProgrammeCard.vue'
import { useProgrammeStore } from 'stores/programme-store'

const isLoading = ref(false)
const page = ref(1)
const programmeStore = useProgrammeStore()

const { programmes, hasNext, hasPrevious } = storeToRefs(programmeStore)

watch(page, async (newPage) => {
  await fetchProgrammes(newPage, 4)
})

const fetchProgrammes = async (newPage: number, limit: number) => {
  isLoading.value = true
  await programmeStore.initProgrammes(
    {
      page: newPage,
      sort: 'is_featured',
      order: 'desc',
      limit: limit,
    },
    { accept_applications: true }
  )
  isLoading.value = false
}

const goToNext = () => {
  if (hasNext.value) {
    page.value++
  }
}

const goToPrevious = () => {
  if (page.value > 1) {
    page.value--
  }
}
</script>

<template>
  <div class="my-section section">
    <div class="container">
      <div class="header">
        <h3 class="title text-primary">{{ $t('home.programmes_section.title') }}</h3>
        <h4 class="subtitle text-primary">{{ $t('home.programmes_section.subtitle') }}</h4>
      </div>

      <div class="navigation-wrapper">
        <q-no-ssr class="nav-btn-prev">
          <q-btn
            flat
            :disable="!hasPrevious"
            @click="goToPrevious"
            icon="chevron_left"
            round
            size="md"
            class="bg-secondary"
            text-color="primary"
            v-if="programmes.length > 0"
          />
        </q-no-ssr>

        <div class="row justify-center q-col-gutter-md full-width nav-content">
          <div
            class="col-12 col-sm-6 col-md-3"
            v-for="(programme, index) in programmes"
            :key="programme.id"
          >
            <IndexProgrammeCard :programme="programme" :index="index" />
          </div>
        </div>

        <q-no-ssr class="nav-btn-next">
          <q-btn
            flat
            :disable="!hasNext"
            @click="goToNext"
            icon="chevron_right"
            round
            text-color="primary"
            size="md"
            class="bg-secondary"
            v-if="programmes.length > 0"
          />
        </q-no-ssr>
      </div>

      <div class="footer q-mt-xl">
        <q-btn
          class="btn-yellow"
          :label="$t('home.programmes_section.btn_label')"
          rounded
          no-caps
          :to="{ name: 'Programmes' }"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.my-section {
  position: relative;
  overflow: hidden;
  padding: 100px 40px;
  background-image: url('src/assets/home/programmes-bg.png');
  background-attachment: fixed; // makes the background stay fixed
  background-size: cover; // ensures it covers the entire section
  background-position: center;

  @media (max-width: 600px) {
    padding: 90px 30px;
    background-attachment: scroll;
    // optional: fixed backgrounds can have issues on mobile
  }
}
.navigation-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 10px;

  .nav-content {
    flex: 1;
    order: 2; // Middle on desktop
  }

  .nav-btn-prev {
    order: 1; // Left on desktop
  }

  .nav-btn-next {
    order: 3; // Right on desktop
  }

  @media (max-width: 1000px) {
    flex-wrap: wrap; // Allow items to move to new lines
    justify-content: center;
    gap: 20px;

    .nav-content {
      flex: 0 0 100%; // Force cards to take full width
      order: 1; // Move cards to the TOP
    }

    .nav-btn-prev {
      order: 2; // Move button BELOW cards
    }

    .nav-btn-next {
      order: 3; // Move button BELOW cards next to the other one
    }
  }
}
</style>
